import React, { useEffect, useState } from "react";
import {
  Flex,
  Stack,
  Textarea,
  Box,
  Text,
} from "@chakra-ui/react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";


import { formatDate } from "utils/dateFormatter";

const STATUS_OPTIONS = [
  {
    label: "Open",
    value: "OPEN",
  },
  {
    label: "In Progress",
    value: "IN_PROGRESS",
  },
  {
    label: "Resolved",
    value: "RESOLVED",
  },
  {
    label: "Closed",
    value: "CLOSED",
  },
];

const UpdateSupportTicketDrawer = ({
  isOpen,
  onClose,
  ticket,
  onUpdateTicket,
}) => {
  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

  const [isEditing, setIsEditing] =
    useState(false);

  const [formData, setFormData] =
    useState({
      ticket_id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      subject: "",
      category: "",
      message: "",
      status: "OPEN",
      admin_notes: "",
      created_at: "",
      attachments: [],
    });

  useEffect(() => {
    if (ticket) {
      setFormData({
        ticket_id: ticket.ticket_id,
        first_name: ticket.first_name || "",
        last_name: ticket.last_name || "",
        email: ticket.email || "",
        phone_number: ticket.phone_number || "",
        subject: ticket.subject || "",
        category: ticket.category || "",
        message: ticket.message || "",
        status: ticket.status || "OPEN",
        admin_notes:
          ticket.admin_notes || "",
        created_at:
          ticket.created_at || "",
        attachments:
          ticket.attachments || [],
      });

      setIsEditing(false);
    }
  }, [ticket]);

  const handleInputChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleSave = async () => {
    await onUpdateTicket(
      formData.ticket_id,
      {
        admin_notes:
          formData.admin_notes,
        status: formData.status,
      }
    );

    setIsEditing(false);
  };

  const buildAttachmentUrl = (
    path
  ) => {
    if (!path) return "";

    return `${BASE_URL.replace(
      /\/$/,
      ""
    )}/${path.replace(/^\/+/, "")}`;
  };

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={() => {
        setIsEditing(false);
        onClose();
      }}
      headerContent={
        <Text
          fontSize="xl"
          fontWeight="bold"
        >
          {isEditing
            ? "Update Support Ticket"
            : "View Support Ticket"}
        </Text>
      }
      bodyContent={
        <Stack spacing={4}>
        <Box
  borderRadius="lg"
  p={5}
  bg="gray.50"
>
  <Text
    fontSize="lg"
    fontWeight="700"
    color="#071D54"
    mb={4}
  >
    Partner Information
  </Text>

  <Stack spacing={4}>
    <Flex gap={4}>
      <Box flex={1}>
                <ReusableInput
                label="First Name"
                value={formData.first_name}
                isReadOnly
                />
            </Box>

            <Box flex={1}>
                <ReusableInput
                label="Last Name"
                value={formData.last_name}
                isReadOnly
                />
            </Box>
            </Flex>

            <ReusableInput
            label="Email Address"
            value={formData.email}
            isReadOnly
            />

            <ReusableInput
            label="Phone Number"
            value={formData.phone_number}
            isReadOnly
            />
        </Stack>
        </Box>
          <ReusableInput
            label="Subject"
            value={formData.subject}
            isReadOnly
          />

          <ReusableInput
            label="Category"
            value={formData.category}
            isReadOnly
          />

          <ReusableInput
            label="Message"
            value={formData.message}
            type="textarea"
            isReadOnly
          />

            <Box
                opacity={isEditing ? 1 : 0.7}
                pointerEvents={isEditing ? "auto" : "none"}
                >
                <ReusableInput
                    label="Status"
                    name="status"
                    type="select"
                    value={formData.status}
                    onChange={handleInputChange}
                    options={STATUS_OPTIONS}
                    isDisabled={!isEditing}
                    required={false}
                />
                </Box>

          <Box>
            <Text
              mb={2}
              fontWeight="500"
              fontSize="sm"
              color={"#8D93A1"}
            >
              Admin Notes
            </Text>

            <Textarea
              name="admin_notes"
              value={
                formData.admin_notes
              }
              onChange={
                handleInputChange
              }
              isReadOnly={
                !isEditing
              }
              placeholder="Enter admin notes..."
            />
          </Box>

          <Box>
  <Text
    fontWeight="500"
    mb={3}
    fontSize="sm"
    color={"#8D93A1"}
  >
    Attachments ({formData.attachments.length})
  </Text>

  <Flex
    gap={4}
    overflowX="auto"
    pb={2}
    css={{
      "&::-webkit-scrollbar": {
        height: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CBD5E0",
        borderRadius: "20px",
      },
    }}
  >
    {formData.attachments.map((attachment, index) => {
      const file =
        attachment.file_url ||
        attachment.file_path ||
        attachment.url ||
        attachment;

      const imageUrl = buildAttachmentUrl(file);

      const isImage =
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file);

      return (
        <Box
          key={index}
          minW="180px"
          maxW="180px"
          borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          flexShrink={0}
        >
          {isImage ? (
            <a
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={imageUrl}
                alt={`Attachment ${index + 1}`}
                style={{
                  width: "180px",
                  height: "180px",
                  objectFit: "cover",
                  display: "block",
                  cursor: "pointer",
                }}
              />
            </a>
          ) : (
            <Box p={4}>
              <Text
                fontSize="sm"
                noOfLines={2}
              >
                {attachment.file_name ||
                  `Attachment ${index + 1}`}
              </Text>
            </Box>
          )}
        </Box>
      );
    })}
  </Flex>
</Box>

          <ReusableInput
            label="Created At"
            value={formatDate(
              formData.created_at
            )}
            isReadOnly
          />
        </Stack>
      }
      footerContent={
        <Stack
          direction="row"
          spacing={4}
          justify="flex-end"
        >
          <GradientButton
            width="100px"
            height="40px"
            fontSize="15px"
            onClick={() => {
              setIsEditing(false);
              onClose();
            }}
          >
            Cancel
          </GradientButton>

          <GradientButton
            width="100px"
            height="40px"
            fontSize="15px"
            onClick={() => {
              if (isEditing) {
                handleSave();
              } else {
                setIsEditing(true);
              }
            }}
          >
            {isEditing
              ? "Save"
              : "Edit"}
          </GradientButton>
        </Stack>
      }
    />
  );
};

export default UpdateSupportTicketDrawer;